import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, ArrowLeft, Send, Clock, Trash2, ArrowUp, ArrowDown, X, Upload, Mic, StopCircle,
  Type, Image as ImageIcon, Video, Music, Copy, Check, Link2, Users,
} from 'lucide-react';
import api from '../../services/api';

const DOMINIO_AGENDA_PUBLICA = 'https://agenda.construtorainbase.com.br';

const STEP_LABELS = { texto: 'Texto', imagem: 'Imagem', video: 'Vídeo', audio: 'Áudio' };
const STEP_ICONS = { texto: Type, imagem: ImageIcon, video: Video, audio: Music };

const STATUS_CAMPANHA = {
  pendente: { label: 'Pronta', color: 'bg-gray-100 text-gray-600' },
  agendada: { label: 'Agendada', color: 'bg-purple-100 text-purple-700' },
  na_fila: { label: 'Na fila', color: 'bg-indigo-100 text-indigo-700' },
  em_andamento: { label: 'Enviando', color: 'bg-blue-100 text-blue-700' },
  pausada: { label: 'Pausada', color: 'bg-yellow-100 text-yellow-700' },
  concluida: { label: 'Concluída', color: 'bg-green-100 text-green-700' },
  cancelada: { label: 'Cancelada', color: 'bg-red-100 text-red-700' },
  erro: { label: 'Erro', color: 'bg-red-100 text-red-700' },
};
const STATUS_CONVITE = {
  pendente: { label: 'Não enviado', color: 'bg-gray-100 text-gray-500' },
  enviado: { label: 'Enviado', color: 'bg-blue-100 text-blue-700' },
  aberto: { label: 'Abriu o link', color: 'bg-amber-100 text-amber-700' },
  confirmado: { label: 'Confirmado', color: 'bg-green-100 text-green-700' },
  erro: { label: 'Erro no envio', color: 'bg-red-100 text-red-700' },
};

function formatWhatsapp(v) {
  const d = (v || '').replace(/\D/g, '');
  return d.length > 11 && d.startsWith('55') ? d.slice(2) : d;
}

function parseManuais(texto) {
  return (texto || '')
    .split(/\r?\n/)
    .map(l => l.trim())
    .filter(Boolean)
    .map(l => {
      const partes = l.split(/[,;\t]/).map(p => p.trim()).filter(Boolean);
      if (partes.length >= 2) return { nome: partes[0], whatsapp: partes[1] };
      return { nome: '', whatsapp: partes[0] || '' };
    })
    .filter(c => c.whatsapp.replace(/\D/g, '').length >= 8);
}

// ── Esteira de mensagens (mesma ideia do EnviosMassa, versão enxuta) ─────────────
function AudioRecorder({ onAudioReady }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const mr = useRef(null); const chunks = useRef([]); const timer = useRef(null);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      chunks.current = [];
      rec.ondataavailable = e => { if (e.data.size > 0) chunks.current.push(e.data); };
      rec.onstop = () => { const blob = new Blob(chunks.current, { type: 'audio/webm' }); onAudioReady({ blob, url: URL.createObjectURL(blob), nome: `audio_${Date.now()}.webm` }); stream.getTracks().forEach(t => t.stop()); };
      rec.start(); mr.current = rec; setRecording(true); setSeconds(0);
      timer.current = setInterval(() => setSeconds(s => s + 1), 1000);
    } catch { alert('Microfone indisponível.'); }
  };
  const stop = () => { mr.current?.stop(); clearInterval(timer.current); setRecording(false); };
  return recording
    ? <button type="button" onClick={stop} className="flex items-center gap-2 px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600"><StopCircle size={13} /> Parar ({seconds}s)</button>
    : <button type="button" onClick={start} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs hover:bg-gray-200"><Mic size={13} /> Gravar áudio</button>;
}

function StepCard({ step, index, total, onUpdate, onRemove, onMove }) {
  const fileRef = useRef(null);
  const Icon = STEP_ICONS[step.tipo];
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    onUpdate({ ...step, arquivo: file, preview: URL.createObjectURL(file), nomeOriginal: file.name });
    e.target.value = '';
  };
  return (
    <div className="border border-gray-200 rounded-xl p-3 bg-white space-y-2">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 bg-gray-100 px-2 py-1 rounded-lg">
          <Icon size={13} className="text-gray-600" />
          <span className="text-xs font-semibold text-gray-600">{index + 1}. {STEP_LABELS[step.tipo]}</span>
        </div>
        <div className="flex-1" />
        <button type="button" onClick={() => onMove(-1)} disabled={index === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowUp size={14} /></button>
        <button type="button" onClick={() => onMove(1)} disabled={index === total - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowDown size={14} /></button>
        <button type="button" onClick={onRemove} className="p-1 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
      </div>

      {step.tipo === 'texto' && (
        <>
          <textarea
            value={step.conteudo || ''}
            onChange={e => onUpdate({ ...step, conteudo: e.target.value })}
            placeholder="Digite a mensagem. Use #primeironome pra personalizar e #linkconvite onde o link do convidado deve aparecer."
            rows={3}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y"
          />
          <div className="flex flex-wrap gap-1.5">
            {['#primeironome', '#linkconvite', '#linkevento', '#periodododia'].map(code => (
              <button
                key={code}
                type="button"
                onClick={() => onUpdate({ ...step, conteudo: (step.conteudo || '') + code })}
                className="text-xs font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded hover:bg-blue-100"
              >
                {code}
              </button>
            ))}
          </div>
        </>
      )}

      {step.tipo === 'imagem' && (
        <div>
          {step.preview
            ? <div className="flex items-center gap-3"><img src={step.preview} alt="" className="h-16 w-16 object-cover rounded-lg border" /><span className="text-xs text-gray-500 truncate flex-1">{step.nomeOriginal}</span><button type="button" onClick={() => onUpdate({ ...step, arquivo: null, preview: null, nomeOriginal: '' })} className="text-gray-400 hover:text-red-500"><X size={14} /></button></div>
            : <button type="button" onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-gray-200 rounded-lg py-4 text-center text-sm text-gray-400 hover:border-blue-300 hover:text-blue-500"><ImageIcon size={20} className="mx-auto mb-1 opacity-50" />Selecionar imagem</button>}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
        </div>
      )}

      {step.tipo === 'video' && (
        <div>
          {step.preview
            ? <div className="flex items-center gap-3"><video src={step.preview} className="h-16 w-24 object-cover rounded-lg border" /><span className="text-xs text-gray-500 truncate flex-1">{step.nomeOriginal}</span><button type="button" onClick={() => onUpdate({ ...step, arquivo: null, preview: null, nomeOriginal: '' })} className="text-gray-400 hover:text-red-500"><X size={14} /></button></div>
            : <button type="button" onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-gray-200 rounded-lg py-4 text-center text-sm text-gray-400 hover:border-blue-300 hover:text-blue-500"><Video size={20} className="mx-auto mb-1 opacity-50" />Selecionar vídeo</button>}
          <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/*" className="hidden" onChange={handleFile} />
        </div>
      )}

      {step.tipo === 'audio' && (
        <div>
          {step.preview
            ? <div className="flex items-center gap-3"><audio src={step.preview} controls className="h-8 flex-1" /><span className="text-xs text-gray-500 truncate max-w-[120px]">{step.nomeOriginal}</span><button type="button" onClick={() => onUpdate({ ...step, arquivo: null, preview: null, nomeOriginal: '' })} className="text-gray-400 hover:text-red-500"><X size={14} /></button></div>
            : <div className="flex items-center gap-2"><button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs hover:bg-gray-200"><Upload size={13} /> Arquivo</button><AudioRecorder onAudioReady={({ blob, url, nome }) => onUpdate({ ...step, arquivo: blob, preview: url, nomeOriginal: nome })} /></div>}
          <input ref={fileRef} type="file" accept=".mp3,.ogg,.opus,.wav,.m4a,.webm" className="hidden" onChange={handleFile} />
        </div>
      )}
    </div>
  );
}

// ── Formulário de nova campanha de convite ──────────────────────────────────────
function NovaCampanhaConvite({ eventos, onBack, onCriada }) {
  const [nome, setNome] = useState('');
  const [origemId, setOrigemId] = useState('');
  const [destinoId, setDestinoId] = useState('');
  const [somenteCompareceu, setSomenteCompareceu] = useState(false);
  const [pularJaConfirmados, setPularJaConfirmados] = useState(true);
  const [pularJaConvidados, setPularJaConvidados] = useState(true);
  const [manuaisTexto, setManuaisTexto] = useState('');
  const [instancia, setInstancia] = useState('');

  // Origens extras — paridade com o "Disparo em massa" do Funil de Leads Corretores. Podem ser
  // combinadas livremente com o evento de origem e a lista manual; os contatos são mesclados e
  // deduplicados por telefone no backend antes do envio.
  const [origensExtras, setOrigensExtras] = useState(new Set()); // 'grupo' | 'grupos_destino' | 'agenda' | 'corretores'
  const [grupoSelecionado, setGrupoSelecionado] = useState('');
  const [selecionadosGrupo, setSelecionadosGrupo] = useState(new Set());
  const [gruposDestinoSelecionados, setGruposDestinoSelecionados] = useState(new Set());
  const [selecionadosAgenda, setSelecionadosAgenda] = useState(new Set());
  const [buscaAgenda, setBuscaAgenda] = useState('');
  const [selecionadosCorretores, setSelecionadosCorretores] = useState(new Set());
  const [buscaCorretores, setBuscaCorretores] = useState('');
  const toggleOrigemExtra = (o) => setOrigensExtras(prev => {
    const n = new Set(prev);
    if (n.has(o)) n.delete(o); else n.add(o);
    return n;
  });
  const [steps, setSteps] = useState([{ tipo: 'texto', conteudo: '' }]);
  const [agendadoPara, setAgendadoPara] = useState('');
  const [delaySeg, setDelaySeg] = useState(6);
  const [delayMsg, setDelayMsg] = useState(3);
  const [pausaACada, setPausaACada] = useState(10);
  const [pausaMin, setPausaMin] = useState(5); // pausa longa entre blocos, em MINUTOS
  const [avancado, setAvancado] = useState(false);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const excelRef = useRef(null);

  const { data: instData } = useQuery({
    queryKey: ['envios-massa-instancias'],
    queryFn: () => api.get('/envios-massa/instancias').then(r => r.data),
    refetchOnWindowFocus: false,
  });
  const instancias = instData?.data || [];
  useEffect(() => { if (instancias.length && !instancia) setInstancia(instancias[0].name); }, [instancias, instancia]);

  const { data: origemDetalhe } = useQuery({
    queryKey: ['evento-detalhe-convite', origemId],
    queryFn: () => api.get(`/eventos/${origemId}`).then(r => r.data.data),
    enabled: !!origemId,
  });
  const confsOrigem = (origemDetalhe?.confirmacoes || []).filter(c => c.status !== 'cancelado');
  const compareceramOrigem = confsOrigem.filter(c => c.status === 'compareceu');

  const manuais = useMemo(() => parseManuais(manuaisTexto), [manuaisTexto]);

  // ── Origens extras: grupos do WhatsApp / agenda da instância / corretores cadastrados ──────
  const { data: gruposData, isLoading: gruposLoading, error: gruposError } = useQuery({
    queryKey: ['convite-grupos', instancia],
    queryFn: () => api.get('/envios-massa/grupos', { params: { instancia } }).then(r => r.data),
    enabled: !!instancia && (origensExtras.has('grupo') || origensExtras.has('grupos_destino')),
    refetchOnWindowFocus: false,
  });
  const grupos = gruposData?.data || [];

  const { data: participantesData, isLoading: participantesLoading, error: participantesError } = useQuery({
    queryKey: ['convite-grupo-participantes', instancia, grupoSelecionado],
    queryFn: () => api.get('/envios-massa/grupos/participantes', { params: { instancia, groupJid: grupoSelecionado } }).then(r => r.data),
    enabled: !!instancia && origensExtras.has('grupo') && !!grupoSelecionado,
    refetchOnWindowFocus: false,
  });
  const participantesGrupo = participantesData?.data || [];
  useEffect(() => { setSelecionadosGrupo(new Set(participantesGrupo.map(p => p.telefone))); /* eslint-disable-next-line */ }, [participantesData]);

  const { data: agendaData, isLoading: agendaLoading, error: agendaError } = useQuery({
    queryKey: ['convite-contatos-agenda', instancia],
    queryFn: () => api.get('/envios-massa/contatos', { params: { instancia } }).then(r => r.data),
    enabled: !!instancia && origensExtras.has('agenda'),
    refetchOnWindowFocus: false,
  });
  const contatosAgendaBrutos = agendaData?.data || [];
  const buscaAgendaTermo = buscaAgenda.trim().toLowerCase();
  const contatosAgendaFiltrados = contatosAgendaBrutos.filter(c =>
    !buscaAgendaTermo || c.nome?.toLowerCase().includes(buscaAgendaTermo) || c.telefone.includes(buscaAgendaTermo)
  );

  const { data: corretoresData, isLoading: corretoresLoading, error: corretoresError } = useQuery({
    queryKey: ['convite-corretores'],
    queryFn: () => api.get('/parceiros').then(r => r.data),
    enabled: origensExtras.has('corretores'),
    refetchOnWindowFocus: false,
  });
  const corretoresComWhatsapp = (corretoresData?.data || []).filter(c => c.whatsapp);
  useEffect(() => { if (corretoresData) setSelecionadosCorretores(new Set(corretoresComWhatsapp.map(c => c.id))); /* eslint-disable-next-line */ }, [corretoresData]);
  const buscaCorretoresTermo = buscaCorretores.trim().toLowerCase();
  const corretoresFiltrados = corretoresComWhatsapp.filter(c =>
    !buscaCorretoresTermo || c.nome?.toLowerCase().includes(buscaCorretoresTermo) || c.imobiliaria_nome?.toLowerCase().includes(buscaCorretoresTermo)
  );

  const toggleSet = (setter) => (val) => setter(prev => {
    const n = new Set(prev);
    if (n.has(val)) n.delete(val); else n.add(val);
    return n;
  });
  const toggleGrupoContato = toggleSet(setSelecionadosGrupo);
  const toggleAgendaContato = toggleSet(setSelecionadosAgenda);
  const toggleCorretor = toggleSet(setSelecionadosCorretores);
  const toggleGrupoDestino = toggleSet(setGruposDestinoSelecionados);

  const contatosGrupo = participantesGrupo.filter(p => selecionadosGrupo.has(p.telefone)).map(p => ({ nome: p.nome, whatsapp: p.telefone }));
  const contatosAgenda = contatosAgendaBrutos.filter(c => selecionadosAgenda.has(c.telefone)).map(c => ({ nome: c.nome, whatsapp: c.telefone }));
  const contatosCorretores = corretoresComWhatsapp.filter(c => selecionadosCorretores.has(c.id)).map(c => ({ nome: c.nome, whatsapp: c.whatsapp }));
  const contatosExtras = [
    ...(origensExtras.has('grupo') ? contatosGrupo : []),
    ...(origensExtras.has('agenda') ? contatosAgenda : []),
    ...(origensExtras.has('corretores') ? contatosCorretores : []),
  ];
  const gruposDestino = origensExtras.has('grupos_destino')
    ? grupos.filter(g => gruposDestinoSelecionados.has(g.id)).map(g => ({ jid: g.id, nome: g.subject }))
    : [];

  const addStep = (tipo) => setSteps(prev => [...prev, tipo === 'texto' ? { tipo, conteudo: '' } : { tipo, arquivo: null, preview: null, nomeOriginal: '' }]);
  const updateStep = (i, s) => setSteps(prev => prev.map((x, ix) => ix === i ? s : x));
  const removeStep = (i) => setSteps(prev => prev.filter((_, ix) => ix !== i));
  const moveStep = (i, dir) => setSteps(prev => {
    const arr = [...prev]; const t = i + dir;
    if (t < 0 || t >= arr.length) return prev;
    [arr[i], arr[t]] = [arr[t], arr[i]];
    return arr;
  });

  const importarExcel = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('excel', file);
      const r = await api.post('/envios-massa/parse-excel', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const linhas = (r.data.data || []).map(c => `${c.nome || ''}, ${c.telefone}`).join('\n');
      setManuaisTexto(prev => (prev ? prev + '\n' : '') + linhas);
    } catch (e) {
      alert('Erro ao ler o Excel: ' + (e?.response?.data?.error || e.message));
    }
  };

  const submeter = async (autostart) => {
    setErro('');
    if (!nome.trim()) return setErro('Dê um nome pra campanha');
    if (!destinoId) return setErro('Selecione o evento de destino');
    if (origemId && origemId === destinoId) return setErro('Origem e destino têm que ser eventos diferentes');
    if (!origemId && manuais.length === 0 && contatosExtras.length === 0 && gruposDestino.length === 0) {
      return setErro('Escolha ao menos uma origem: evento de origem, lista manual, grupo do WhatsApp, agenda, corretores ou grupo de destino');
    }
    if (origensExtras.has('grupo') && !grupoSelecionado) return setErro('Selecione o grupo do WhatsApp pra puxar os contatos');
    if (origensExtras.has('grupos_destino') && gruposDestino.length === 0) return setErro('Selecione ao menos um grupo de destino');
    if (!instancia) return setErro('Selecione a instância do WhatsApp');
    if (steps.length === 0) return setErro('Monte a mensagem do convite');
    const temConteudo = steps.some(s => s.tipo === 'texto' ? (s.conteudo || '').trim() : s.arquivo);
    if (!temConteudo) return setErro('Adicione conteúdo aos blocos da mensagem');

    setEnviando(true);
    try {
      const stepsConfig = steps.map(s => s.tipo === 'texto'
        ? { tipo: 'texto', conteudo: s.conteudo }
        : { tipo: s.tipo, nomeOriginal: s.nomeOriginal || '' });
      const config = {
        nome: nome.trim(),
        instancia_evolution: instancia,
        instancia_tipo: 'evolution',
        evento_origem_id: origemId || null,
        evento_destino_id: destinoId,
        somente_compareceu: somenteCompareceu,
        pular_ja_confirmados_destino: pularJaConfirmados,
        pular_ja_convidados: pularJaConvidados,
        contatos_manuais: manuais,
        contatos_extras: contatosExtras,
        grupos_destino: gruposDestino,
        steps: stepsConfig,
        delay_segundos: Number(delaySeg),
        delay_entre_mensagens_segundos: Number(delayMsg),
        pausa_a_cada: Number(pausaACada),
        duracao_pausa_segundos: Math.round(Number(pausaMin) * 60),
        autostart,
        agendado_para: agendadoPara ? new Date(agendadoPara).toISOString() : null,
      };
      const fd = new FormData();
      fd.append('config', JSON.stringify(config));
      steps.forEach((s, i) => { if (s.tipo !== 'texto' && s.arquivo) fd.append(`step_${i}`, s.arquivo, s.nomeOriginal || `step_${i}`); });

      const r = await api.post('/envios-massa/convite', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = r.data.data;
      let msg = `Campanha criada com ${d.convidados ?? d.total} convidado(s)`;
      if (d.grupos_destino) msg += ` + ${d.grupos_destino} grupo(s) de destino`;
      msg += '.';
      if (d.ignorados_ja_confirmados) msg += ` ${d.ignorados_ja_confirmados} já estavam confirmados no destino e foram ignorados.`;
      if (d.ignorados_ja_convidados) msg += ` ${d.ignorados_ja_convidados} já tinham recebido convite pra esse evento antes.`;
      if (d.ignorados_limite) msg += ` ${d.ignorados_limite} ficaram de fora: o evento atingiu o limite total de participantes.`;
      if (d.ignorados_blacklist) msg += ` ${d.ignorados_blacklist} na blacklist.`;
      if (d.agendada) msg += ' Vai disparar na data agendada.';
      else if (d.enfileirada) msg += ' Entrou na fila (instância ocupada).';
      else if (autostart) msg += ' Disparo iniciado.';
      alert(msg);
      onCriada();
    } catch (e) {
      setErro(e?.response?.data?.error || 'Erro ao criar a campanha de convite');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"><ArrowLeft size={14} /> Voltar</button>

      <div className="bg-white rounded-xl shadow p-6 space-y-4">
        <h2 className="text-lg font-bold text-gray-800">Nova campanha de convite</h2>

        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nome da campanha *</label>
          <input className="input-field" placeholder="ex: Convite Parrilla — inscritos do Lumière" value={nome} onChange={e => setNome(e.target.value)} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Evento de origem (quem convidar)</label>
            <select className="input-field" value={origemId} onChange={e => setOrigemId(e.target.value)}>
              <option value="">— só lista manual —</option>
              {eventos.filter(e => e.id !== destinoId).map(e => (
                <option key={e.id} value={e.id}>{e.nome} ({e.total_confirmados} confirmados)</option>
              ))}
            </select>
            {origemId && (
              <p className="text-xs text-gray-400 mt-1">
                {confsOrigem.length} confirmados · {compareceramOrigem.length} compareceram
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Evento de destino (pra onde vão) *</label>
            <select className="input-field" value={destinoId} onChange={e => setDestinoId(e.target.value)}>
              <option value="">Selecione...</option>
              {eventos.filter(e => e.id !== origemId).map(e => (
                <option key={e.id} value={e.id}>{e.nome}</option>
              ))}
            </select>
          </div>
        </div>

        {origemId && (
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={somenteCompareceu} onChange={e => setSomenteCompareceu(e.target.checked)} />
            Convidar só quem <strong>compareceu</strong> no evento de origem
          </label>
        )}
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={pularJaConfirmados} onChange={e => setPularJaConfirmados(e.target.checked)} />
          Não enviar pra quem já está confirmado no evento de destino
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={pularJaConvidados} onChange={e => setPularJaConvidados(e.target.checked)} />
          Não reenviar pra quem já recebeu convite deste evento em campanhas anteriores
        </label>

        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Lista manual (opcional)</label>
          <textarea
            className="input-field font-mono text-xs"
            rows={3}
            placeholder={'Um por linha: Nome, WhatsApp\nex: Maria Souza, 44999998888'}
            value={manuaisTexto}
            onChange={e => setManuaisTexto(e.target.value)}
          />
          <div className="flex items-center gap-3 mt-1">
            <button type="button" onClick={() => excelRef.current?.click()} className="text-xs text-blue-600 hover:underline flex items-center gap-1"><Upload size={12} /> Importar de Excel</button>
            <span className="text-xs text-gray-400">{manuais.length} contato(s) válido(s) na lista manual</span>
            <input ref={excelRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={e => importarExcel(e.target.files?.[0])} />
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Instância do WhatsApp (Evolution API) *</label>
          <select className="input-field" value={instancia} onChange={e => setInstancia(e.target.value)}>
            <option value="">Selecione a instância...</option>
            {instancias.map(i => <option key={i.name} value={i.name}>{i.name}</option>)}
          </select>
        </div>

        {/* ── Origens extras (podem ser combinadas com o evento de origem + lista manual) ── */}
        <div className="border-t border-gray-100 pt-4">
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Outras origens de contatos (opcional, pode combinar)</label>
          <div className="flex flex-wrap gap-2">
            {[
              ['grupo', 'Contatos de um grupo'],
              ['agenda', 'Agenda da instância'],
              ['corretores', 'Corretores cadastrados'],
              ['grupos_destino', 'Enviar direto pra grupo(s)'],
            ].map(([chave, label]) => (
              <button
                key={chave}
                type="button"
                onClick={() => toggleOrigemExtra(chave)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${origensExtras.has(chave) ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
              >
                {label}
              </button>
            ))}
          </div>
          {(contatosExtras.length > 0 || gruposDestino.length > 0) && (
            <p className="text-xs text-gray-500 mt-1">
              {contatosExtras.length > 0 && <>{contatosExtras.length} contato(s) das origens extras</>}
              {contatosExtras.length > 0 && gruposDestino.length > 0 && ' · '}
              {gruposDestino.length > 0 && <>{gruposDestino.length} grupo(s) de destino</>}
              {' '}(duplicados entre origens são mesclados automaticamente).
            </p>
          )}
        </div>

        {(origensExtras.has('grupo') || origensExtras.has('grupos_destino')) && !instancia && (
          <p className="text-xs text-amber-600">Selecione a instância acima pra carregar os grupos.</p>
        )}

        {origensExtras.has('grupo') && (
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <label className="block text-xs font-bold text-gray-500 uppercase">Grupo do WhatsApp (puxa os participantes)</label>
            {gruposLoading ? (
              <p className="text-xs text-gray-400">Carregando grupos...</p>
            ) : gruposError ? (
              <p className="text-xs text-red-500">{gruposError.response?.data?.error || 'Erro ao buscar grupos'}</p>
            ) : (
              <select className="input-field" value={grupoSelecionado} onChange={e => setGrupoSelecionado(e.target.value)}>
                <option value="">Selecione um grupo...</option>
                {grupos.map(g => <option key={g.id} value={g.id}>{g.subject} ({g.size})</option>)}
              </select>
            )}
            {grupoSelecionado && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-gray-500">{selecionadosGrupo.size}/{participantesGrupo.length} selecionados</span>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setSelecionadosGrupo(new Set(participantesGrupo.map(p => p.telefone)))} className="text-xs font-medium text-blue-600 hover:text-blue-700">Marcar todos</button>
                    <button type="button" onClick={() => setSelecionadosGrupo(new Set())} className="text-xs font-medium text-blue-600 hover:text-blue-700">Desmarcar todos</button>
                  </div>
                </div>
                {participantesLoading ? (
                  <p className="text-xs text-gray-400">Carregando participantes...</p>
                ) : participantesError ? (
                  <p className="text-xs text-red-500">{participantesError.response?.data?.error || 'Erro ao buscar participantes'}</p>
                ) : participantesGrupo.length === 0 ? (
                  <p className="text-xs text-gray-400">Nenhum participante com telefone válido nesse grupo.</p>
                ) : (
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto bg-white">
                    {participantesGrupo.map(p => (
                      <label key={p.telefone} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer">
                        <input type="checkbox" checked={selecionadosGrupo.has(p.telefone)} onChange={() => toggleGrupoContato(p.telefone)} className="shrink-0" />
                        <span className="flex-1 truncate">{p.nome || 'Sem nome salvo'}</span>
                        <span className="text-xs text-gray-400 shrink-0">{formatWhatsapp(p.telefone)}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {origensExtras.has('agenda') && (
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-bold text-gray-500 uppercase">Agenda da instância ({selecionadosAgenda.size} selecionado(s))</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setSelecionadosAgenda(prev => new Set([...prev, ...contatosAgendaFiltrados.map(c => c.telefone)]))} className="text-xs font-medium text-blue-600 hover:text-blue-700">Marcar visíveis</button>
                <button type="button" onClick={() => setSelecionadosAgenda(prev => { const f = new Set(contatosAgendaFiltrados.map(c => c.telefone)); return new Set([...prev].filter(t => !f.has(t))); })} className="text-xs font-medium text-blue-600 hover:text-blue-700">Desmarcar visíveis</button>
              </div>
            </div>
            <p className="text-xs text-gray-500">Contatos com quem essa instância já trocou mensagem direta. "Marcar/desmarcar" afeta só o que estiver visível na busca.</p>
            <input type="text" value={buscaAgenda} onChange={e => setBuscaAgenda(e.target.value)} placeholder="Buscar por nome ou telefone..." className="input-field text-sm" />
            {agendaLoading ? (
              <p className="text-xs text-gray-400">Carregando agenda...</p>
            ) : agendaError ? (
              <p className="text-xs text-red-500">{agendaError.response?.data?.error || 'Erro ao buscar agenda'}</p>
            ) : contatosAgendaFiltrados.length === 0 ? (
              <p className="text-xs text-gray-400">Nenhum contato encontrado.</p>
            ) : (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto bg-white">
                {contatosAgendaFiltrados.map(c => (
                  <label key={c.telefone} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer">
                    <input type="checkbox" checked={selecionadosAgenda.has(c.telefone)} onChange={() => toggleAgendaContato(c.telefone)} className="shrink-0" />
                    <span className="flex-1 truncate">{c.nome || 'Sem nome salvo'}</span>
                    <span className="text-xs text-gray-400 shrink-0">{formatWhatsapp(c.telefone)}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {origensExtras.has('corretores') && (
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-bold text-gray-500 uppercase">Corretores cadastrados ({selecionadosCorretores.size}/{corretoresFiltrados.length} selecionados)</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setSelecionadosCorretores(new Set(corretoresFiltrados.map(c => c.id)))} className="text-xs font-medium text-blue-600 hover:text-blue-700">Marcar todos</button>
                <button type="button" onClick={() => setSelecionadosCorretores(new Set())} className="text-xs font-medium text-blue-600 hover:text-blue-700">Desmarcar todos</button>
              </div>
            </div>
            <input type="text" value={buscaCorretores} onChange={e => setBuscaCorretores(e.target.value)} placeholder="Buscar por nome ou imobiliária..." className="input-field text-sm" />
            {corretoresLoading ? (
              <p className="text-xs text-gray-400">Carregando corretores...</p>
            ) : corretoresError ? (
              <p className="text-xs text-red-500">{corretoresError.response?.data?.error || 'Erro ao buscar corretores'}</p>
            ) : corretoresFiltrados.length === 0 ? (
              <p className="text-xs text-gray-400">Nenhum corretor com WhatsApp encontrado.</p>
            ) : (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto bg-white">
                {corretoresFiltrados.map(c => (
                  <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer">
                    <input type="checkbox" checked={selecionadosCorretores.has(c.id)} onChange={() => toggleCorretor(c.id)} className="shrink-0" />
                    <span className="flex-1 truncate">{c.nome}</span>
                    <span className="text-xs text-gray-400 truncate max-w-[140px]">{c.imobiliaria_nome || ''}</span>
                    <span className="text-xs text-gray-400 shrink-0">{formatWhatsapp(c.whatsapp)}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {origensExtras.has('grupos_destino') && (
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-bold text-gray-500 uppercase">Grupos de destino ({gruposDestinoSelecionados.size}/{grupos.length} selecionados)</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setGruposDestinoSelecionados(new Set(grupos.map(g => g.id)))} className="text-xs font-medium text-blue-600 hover:text-blue-700">Marcar todos</button>
                <button type="button" onClick={() => setGruposDestinoSelecionados(new Set())} className="text-xs font-medium text-blue-600 hover:text-blue-700">Desmarcar todos</button>
              </div>
            </div>
            <p className="text-xs text-gray-500">A mensagem vai <strong>uma vez direto no chat</strong> de cada grupo — não extrai os contatos. Como não há convidado individual, o <code className="font-mono">#linkconvite</code> vira o link genérico de inscrição do evento (o mesmo que <code className="font-mono">#linkevento</code>).</p>
            {gruposLoading ? (
              <p className="text-xs text-gray-400">Carregando grupos...</p>
            ) : gruposError ? (
              <p className="text-xs text-red-500">{gruposError.response?.data?.error || 'Erro ao buscar grupos'}</p>
            ) : grupos.length === 0 ? (
              <p className="text-xs text-gray-400">Nenhum grupo encontrado nessa instância.</p>
            ) : (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto bg-white">
                {grupos.map(g => (
                  <label key={g.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer">
                    <input type="checkbox" checked={gruposDestinoSelecionados.has(g.id)} onChange={() => toggleGrupoDestino(g.id)} className="shrink-0" />
                    <span className="flex-1 truncate">{g.subject}</span>
                    <span className="text-xs text-gray-400 shrink-0">{g.size} membros</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs font-bold text-gray-500 uppercase">Mensagem do convite</label>
            <span className="text-xs text-gray-400">{steps.length} bloco(s)</span>
          </div>
          <p className="text-xs text-gray-400 mb-2">
            Cada convidado recebe os blocos em sequência. O <code className="font-mono">#linkconvite</code> vira o link
            personalizado dele (já preenchido com os dados). Se você não usar o atalho, o link entra automaticamente no fim.
            Em grupo de destino, <code className="font-mono">#linkconvite</code> cai no link genérico de inscrição do evento.
          </p>
          <div className="flex flex-wrap gap-2 mb-2">
            <button type="button" onClick={() => addStep('texto')} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100"><Type size={13} /> + Texto</button>
            <button type="button" onClick={() => addStep('imagem')} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-medium hover:bg-green-100"><ImageIcon size={13} /> + Imagem</button>
            <button type="button" onClick={() => addStep('video')} className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium hover:bg-purple-100"><Video size={13} /> + Vídeo</button>
            <button type="button" onClick={() => addStep('audio')} className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-700 rounded-lg text-xs font-medium hover:bg-orange-100"><Music size={13} /> + Áudio</button>
          </div>
          <div className="space-y-2">
            {steps.map((step, i) => (
              <StepCard key={i} step={step} index={i} total={steps.length} onUpdate={s => updateStep(i, s)} onRemove={() => removeStep(i)} onMove={dir => moveStep(i, dir)} />
            ))}
            {steps.length === 0 && <div className="py-6 text-center text-gray-300 text-sm">Nenhum bloco. Use os botões acima.</div>}
          </div>
        </div>

        <button type="button" onClick={() => setAvancado(v => !v)} className="text-xs text-gray-500 hover:text-gray-700">
          {avancado ? '− Esconder' : '+ Mostrar'} opções avançadas (ritmo de envio, blocos, agendamento)
        </button>
        {avancado && (
          <div className="bg-gray-50 rounded-lg p-3 space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div><label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Intervalo entre mensagens (s)</label>
                <input type="number" min={1} className="input-field text-sm" value={delayMsg} onChange={e => setDelayMsg(e.target.value)} />
                <p className="text-[10px] text-gray-400 mt-0.5">entre os blocos da esteira, pro mesmo convidado</p></div>
              <div><label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Intervalo entre convidados (s)</label>
                <input type="number" min={2} className="input-field text-sm" value={delaySeg} onChange={e => setDelaySeg(e.target.value)} />
                <p className="text-[10px] text-gray-400 mt-0.5">de um convidado pro próximo</p></div>
              <div><label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Agendar disparo</label>
                <input type="datetime-local" className="input-field text-sm" value={agendadoPara} onChange={e => setAgendadoPara(e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3 border-t border-gray-200 pt-3">
              <div><label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Bloco de convidados</label>
                <input type="number" min={0} className="input-field text-sm" value={pausaACada} onChange={e => setPausaACada(e.target.value)} />
                <p className="text-[10px] text-gray-400 mt-0.5">envia esse tanto e faz uma pausa longa (0 = sem pausa)</p></div>
              <div><label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Pausa entre blocos (min)</label>
                <input type="number" min={0} step="0.5" className="input-field text-sm" value={pausaMin} onChange={e => setPausaMin(e.target.value)} />
                <p className="text-[10px] text-gray-400 mt-0.5">quanto espera antes de retomar o próximo bloco</p></div>
            </div>
          </div>
        )}

        {erro && <p className="text-sm text-red-600">{erro}</p>}

        <div className="flex gap-3 pt-2 border-t border-gray-100">
          <button onClick={() => submeter(false)} disabled={enviando} className="btn-secondary flex items-center gap-2 disabled:opacity-50">
            <Clock size={15} /> Só criar
          </button>
          <button onClick={() => submeter(true)} disabled={enviando} className="btn-primary flex items-center gap-2 disabled:opacity-50">
            <Send size={15} /> {enviando ? 'Criando...' : (agendadoPara ? 'Criar e agendar' : 'Criar e disparar')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Detalhe de uma campanha de convite ─────────────────────────────────────────
function CampanhaConviteDetalhe({ campanhaId, onBack }) {
  const qc = useQueryClient();
  const [copiado, setCopiado] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['convite-detalhe', campanhaId],
    queryFn: () => api.get(`/envios-massa/convite/${campanhaId}`).then(r => r.data.data),
    refetchInterval: 15000,
  });

  const acaoMut = useMutation({
    mutationFn: (acao) => acao === 'cancelar'
      ? api.delete(`/envios-massa/${campanhaId}`)
      : api.post(`/envios-massa/${campanhaId}/${acao}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['convite-detalhe', campanhaId] }),
    onError: (err) => alert(err?.response?.data?.error || 'Erro'),
  });

  const copiarLink = async (token) => {
    const link = `${DOMINIO_AGENDA_PUBLICA}/publico/convite/${token}`;
    try { await navigator.clipboard.writeText(link); setCopiado(token); setTimeout(() => setCopiado(null), 2000); } catch { /* noop */ }
  };

  if (isLoading || !data) return <div className="text-center text-gray-400 py-12">Carregando...</div>;

  const convites = data.convites || [];
  const gruposDestino = data.grupos_destino || [];
  const confirmados = convites.filter(c => c.status === 'confirmado').length;
  const abertos = convites.filter(c => c.status === 'aberto').length;
  const enviados = convites.filter(c => ['enviado', 'aberto', 'confirmado'].includes(c.envio_status) || ['aberto', 'confirmado'].includes(c.status)).length;
  const st = STATUS_CAMPANHA[data.status] || { label: data.status, color: 'bg-gray-100 text-gray-600' };
  const podeControlar = ['pendente', 'em_andamento', 'pausada', 'na_fila', 'agendada'].includes(data.status);

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"><ArrowLeft size={14} /> Voltar</button>

      <div className="bg-white rounded-xl shadow p-6 mb-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800">{data.nome}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {data.evento_origem_nome ? <>De <strong>{data.evento_origem_nome}</strong> → </> : 'Lista manual → '}
              <strong>{data.evento_destino_nome}</strong> · instância <strong>{data.instancia_evolution}</strong>
            </p>
          </div>
          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${st.color}`}>{st.label}</span>
        </div>

        <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-gray-100 text-sm text-gray-600">
          <span className="flex items-center gap-1"><Users size={15} /> <strong>{convites.length}</strong> convidados</span>
          {gruposDestino.length > 0 && <span>+ {gruposDestino.length} grupo(s) de destino</span>}
          <span>{enviados} enviados</span>
          <span className="text-amber-600">{abertos} abriram</span>
          <span className="text-green-600"><strong>{confirmados}</strong> confirmados</span>
          {podeControlar && (
            <div className="flex gap-2 ml-auto">
              {(data.status === 'pendente' || data.status === 'agendada') && (
                <button onClick={() => acaoMut.mutate('iniciar')} className="btn-primary text-sm py-1.5 flex items-center gap-1"><Send size={13} /> Disparar agora</button>
              )}
              {data.status === 'em_andamento' && (
                <button onClick={() => acaoMut.mutate('pausar')} className="btn-secondary text-sm py-1.5">Pausar</button>
              )}
              {data.status === 'pausada' && (
                <button onClick={() => acaoMut.mutate('retomar')} className="btn-primary text-sm py-1.5">Retomar</button>
              )}
              <button onClick={() => { if (window.confirm('Cancelar o disparo desta campanha?')) acaoMut.mutate('cancelar'); }} className="text-sm py-1.5 px-3 text-red-500 hover:text-red-700">Cancelar</button>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b"><tr>
            <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Nome</th>
            <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">WhatsApp</th>
            <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Envio</th>
            <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Convite</th>
            <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Link personalizado</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-100">
            {convites.map(c => {
              const envio = STATUS_CONVITE[c.envio_status === 'enviado' ? 'enviado' : (c.envio_status === 'erro' ? 'erro' : 'pendente')];
              const conv = STATUS_CONVITE[c.status] || STATUS_CONVITE.pendente;
              return (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{c.nome || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{formatWhatsapp(c.whatsapp)}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${envio.color}`}>{envio.label}</span></td>
                  <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${conv.color}`}>{conv.label}</span></td>
                  <td className="px-4 py-3">
                    <button onClick={() => copiarLink(c.token)} className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-xs">
                      {copiado === c.token ? <><Check size={13} /> Copiado!</> : <><Copy size={13} /> Copiar link</>}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {gruposDestino.length > 0 && (
        <div className="bg-white rounded-xl shadow overflow-hidden mt-4">
          <div className="px-4 py-3 border-b bg-gray-50 text-xs font-semibold text-gray-500 uppercase">Enviado direto para grupos</div>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-100">
              {gruposDestino.map(g => {
                const envio = STATUS_CONVITE[g.status === 'enviado' ? 'enviado' : (g.status === 'erro' ? 'erro' : 'pendente')];
                return (
                  <tr key={g.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{g.nome || 'Grupo'}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{g.telefone}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${envio.color}`}>{envio.label}</span>
                      {g.erro_msg && <span className="text-xs text-red-500 ml-2">{g.erro_msg}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Aba principal ─────────────────────────────────────────────────────────────
export default function ConvitesTab() {
  const [view, setView] = useState('lista'); // 'lista' | 'nova' | id
  const qc = useQueryClient();

  const { data: eventosData } = useQuery({
    queryKey: ['eventos-lista'],
    queryFn: () => api.get('/eventos').then(r => r.data),
  });
  const eventos = eventosData?.data || [];

  const { data: campanhasData, isLoading } = useQuery({
    queryKey: ['convites-lista'],
    queryFn: () => api.get('/envios-massa/convite').then(r => r.data),
    refetchInterval: 20000,
  });
  const campanhas = campanhasData?.data || [];

  if (view === 'nova') {
    return (
      <NovaCampanhaConvite
        eventos={eventos}
        onBack={() => setView('lista')}
        onCriada={() => { qc.invalidateQueries({ queryKey: ['convites-lista'] }); setView('lista'); }}
      />
    );
  }
  if (view !== 'lista') {
    return <CampanhaConviteDetalhe campanhaId={view} onBack={() => setView('lista')} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Link2 size={15} /> Convide os inscritos de um evento para outro, com link individual já preenchido.
        </div>
        <button onClick={() => setView('nova')} className="btn-primary flex items-center gap-2"><Plus size={16} /> Nova campanha</button>
      </div>

      {isLoading ? (
        <div className="text-center text-gray-400 py-12">Carregando...</div>
      ) : campanhas.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center text-gray-400">
          Nenhuma campanha de convite ainda. Crie a primeira com "Nova campanha".
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b"><tr>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Campanha</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Origem → Destino</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Status</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Enviados</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Confirmados</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {campanhas.map(c => {
                const st = STATUS_CAMPANHA[c.status] || { label: c.status, color: 'bg-gray-100 text-gray-600' };
                return (
                  <tr key={c.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setView(c.id)}>
                    <td className="px-4 py-3 font-medium">{c.nome}</td>
                    <td className="px-4 py-3 text-gray-500">{c.evento_origem_nome || 'Lista manual'} → {c.evento_destino_nome || '—'}</td>
                    <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${st.color}`}>{st.label}</span></td>
                    <td className="px-4 py-3 text-gray-500">{c.enviados}/{c.total}</td>
                    <td className="px-4 py-3 font-medium text-green-600">{c.confirmados}<span className="text-gray-400 font-normal"> · {c.abertos} abriram</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
